import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useLanguage } from '../../contexts/LanguageContext'

const GENDER_OPTIONS = [
  { value: 'male', labelKey: 'footer.newsletterGenderMale' },
  { value: 'female', labelKey: 'footer.newsletterGenderFemale' },
]

export default function NewsletterSubscribeModal({
  open,
  email,
  submitting = false,
  onClose,
  onConfirm,
}) {
  const { t } = useLanguage()
  const [name, setName] = useState('')
  const [gender, setGender] = useState('')

  useEffect(() => {
    if (!open) return undefined

    setName('')
    setGender('')

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !submitting) onClose()
    }

    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onClose, submitting])

  const handleSubmit = (event) => {
    event.preventDefault()
    const trimmedName = name.trim()
    if (!trimmedName || !gender) return
    onConfirm({ name: trimmedName, gender })
  }

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="newsletter-modal-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-brand-navy/55 backdrop-blur-[2px]"
        onClick={() => {
          if (!submitting) onClose()
        }}
        aria-label={t('common.close')}
      />

      <div className="relative w-full max-w-md bg-white shadow-[0_24px_48px_-12px_rgba(16,34,64,0.28)]">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center text-slate-400 transition-colors hover:text-brand-navy disabled:opacity-50"
          aria-label={t('common.close')}
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <form onSubmit={handleSubmit} className="px-8 pb-8 pt-10">
          <h3 id="newsletter-modal-title" className="font-heading text-xl font-bold text-brand-navy">
            {t('footer.newsletterModalTitle')}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">{t('footer.newsletterModalLead')}</p>
          {email ? (
            <p className="mt-3 text-xs uppercase tracking-[0.12em] text-slate-500">{email}</p>
          ) : null}

          <div className="mt-6">
            <label htmlFor="newsletter-name" className="mb-2 block text-sm font-medium text-brand-navy">
              {t('footer.newsletterNameLabel')}
            </label>
            <input
              id="newsletter-name"
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('footer.newsletterNamePlaceholder')}
              required
              autoComplete="given-name"
              className="w-full rounded-none border border-gray-300 bg-white px-4 py-3 text-gray-900 placeholder-gray-500 transition-all duration-200 focus:border-brand-navy/60 focus:outline-none focus:ring-1 focus:ring-brand-navy/30"
            />
          </div>

          <fieldset className="mt-5">
            <legend className="mb-2 block text-sm font-medium text-brand-navy">
              {t('footer.newsletterGenderLabel')}
            </legend>
            <div className="grid grid-cols-2 gap-3">
              {GENDER_OPTIONS.map((option) => {
                const selected = gender === option.value
                return (
                  <label
                    key={option.value}
                    className={`flex cursor-pointer items-center justify-center rounded-none border px-4 py-3 text-sm font-semibold transition-colors ${
                      selected
                        ? 'border-brand-navy bg-brand-navy text-white'
                        : 'border-gray-300 bg-white text-brand-navy hover:border-brand-navy/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="newsletter-gender"
                      value={option.value}
                      checked={selected}
                      onChange={() => setGender(option.value)}
                      className="sr-only"
                    />
                    {t(option.labelKey)}
                  </label>
                )
              })}
            </div>
          </fieldset>

          <button
            type="submit"
            disabled={submitting || !name.trim() || !gender}
            className="mt-8 w-full rounded-none bg-brand-navy px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-[#0c1a30] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? t('footer.newsletterSubmitting') : t('footer.newsletterModalSubmit')}
          </button>
        </form>
      </div>
    </div>,
    document.body
  )
}
